import { lazy } from 'react';
import { Outlet } from 'react-router-dom';

// project imports
import Loadable from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';
import AuthGuard from 'utils/AuthGuard';
import RouteGuard from 'components/RouteGuard';
import { VisaoCalendario, VisaoGantt, VisaoKanban, VisaoLista } from '../pages/projetos/visoesDoProjeto';

// render - Dashboard
const HomePage = Loadable(lazy(() => import('../pages/dashboard/HomePage')));

// render - Agenda Module
const AgendaPage = Loadable(lazy(() => import('../pages/agenda/AgendaPage')));

// render - Auth Module
const UsersListPage = Loadable(lazy(() => import('../pages/auth/users/UsersListPage')));
const RolesListPage = Loadable(lazy(() => import('../pages/auth/roles/RolesListPage')));
const PermissionsListPage = Loadable(lazy(() => import('../pages/auth/permissions/PermissionsListPage')));
const AuthPlaceholderPage = Loadable(lazy(() => import('../pages/auth/PlaceholderPage')));

// render - Cadastros Module
const PessoasListPage = Loadable(lazy(() => import('../pages/cadastros/pessoas/PessoasListPage')));
const PessoaDetailPage = Loadable(lazy(() => import('../pages/cadastros/pessoas/PessoaDetailPage')));
const CatalogosHubPage = Loadable(lazy(() => import('../pages/cadastros/catalogos/CatalogosHubPage')));
const CatalogoCrudPage = Loadable(lazy(() => import('../pages/cadastros/catalogos/CatalogoCrudPage')));
const PessoasImportacaoPage = Loadable(lazy(() => import('../pages/cadastros/importacao/ImportacaoPage')));

// Cofre de senhas
const CofrePage = Loadable(lazy(() => import('../pages/cofre/CofrePage')));
const CofreAuditoriaPage = Loadable(lazy(() => import('../pages/cofre/CofreAuditoriaPage')));
const CofreLixeiraPage = Loadable(lazy(() => import('../pages/cofre/CofreLixeiraPage')));

// render - Financeiro Module
const DashboardFinanceiroPage = Loadable(lazy(() => import('../pages/financeiro/dashboard/DashboardFinanceiroPage')));
const LancamentosListPage = Loadable(lazy(() => import('../pages/financeiro/lancamentos/LancamentosListPage')));
const FaturasListPage = Loadable(lazy(() => import('../pages/financeiro/faturas/FaturasListPage')));
const PainelFaturamentoPage = Loadable(lazy(() => import('../pages/financeiro/faturamento/PainelFaturamentoPage')));
const FaturamentoPerfisPage = Loadable(lazy(() => import('../pages/financeiro/faturamento/perfis/FaturamentoPerfisPage')));
const AtividadesFiscaisPage = Loadable(lazy(() => import('../pages/financeiro/faturamento/atividades/AtividadesFiscaisPage')));
const AsaasIntegracaoPage = Loadable(lazy(() => import('../pages/financeiro/integracoes/asaas/AsaasIntegracaoPage')));
const FaturaDetailPage = Loadable(lazy(() => import('../pages/financeiro/faturas/FaturaDetailPage')));
const ContasPagarListPage = Loadable(lazy(() => import('../pages/financeiro/contas-pagar/ContasPagarListPage')));
const ContasReceberListPage = Loadable(lazy(() => import('../pages/financeiro/contas-receber/ContasReceberListPage')));
const PagamentosPage = Loadable(lazy(() => import('../pages/financeiro/pagamentos/PagamentosPage')));
const CategoriasPage = Loadable(lazy(() => import('../pages/financeiro/categorias/CategoriasPage')));
const CentrosCustoPage = Loadable(lazy(() => import('../pages/financeiro/centros-custo/CentrosCustoPage')));
const ContasBancariasPage = Loadable(lazy(() => import('../pages/financeiro/contas-bancarias/ContasBancariasPage')));
const InvestimentosPage = Loadable(lazy(() => import('../pages/financeiro/investimentos/InvestimentosPage')));
const BancosPage = Loadable(lazy(() => import('../pages/financeiro/bancos/BancosPage')));
const ConciliacaoPage = Loadable(lazy(() => import('../pages/financeiro/conciliacao/ConciliacaoPage')));
const FechamentosPage = Loadable(lazy(() => import('../pages/financeiro/conciliacao/FechamentosPage')));
const ImportacaoPage = Loadable(lazy(() => import('../pages/financeiro/importacao/ImportacaoPage')));
const OrcamentoPage = Loadable(lazy(() => import('../pages/financeiro/orcamento/OrcamentoPage')));
const RelatoriosHubPage = Loadable(lazy(() => import('../pages/financeiro/relatorios/RelatoriosHubPage')));
const FluxoCaixaPage = Loadable(lazy(() => import('../pages/financeiro/relatorios/FluxoCaixaPage')));
const DREPage = Loadable(lazy(() => import('../pages/financeiro/relatorios/DREPage')));
const AgingPage = Loadable(lazy(() => import('../pages/financeiro/relatorios/AgingPage')));
const PorCategoriaPage = Loadable(lazy(() => import('../pages/financeiro/relatorios/PorCategoriaPage')));
const PorCentroCustoPage = Loadable(lazy(() => import('../pages/financeiro/relatorios/PorCentroCustoPage')));
const PrevistoRealizadoPage = Loadable(lazy(() => import('../pages/financeiro/relatorios/PrevistoRealizadoPage')));
const OrcadoRealizadoPage = Loadable(lazy(() => import('../pages/financeiro/relatorios/OrcadoRealizadoPage')));
const EvolucaoMensalPage = Loadable(lazy(() => import('../pages/financeiro/relatorios/EvolucaoMensalPage')));
const SaldosContasPage = Loadable(lazy(() => import('../pages/financeiro/relatorios/SaldosContasPage')));
const PosicoesInvestimentosPage = Loadable(lazy(() => import('../pages/financeiro/relatorios/PosicoesInvestimentosPage')));
const PatrimonioConsolidadoPage = Loadable(lazy(() => import('../pages/financeiro/relatorios/PatrimonioConsolidadoPage')));
const CarteiraInvestimentosPage = Loadable(lazy(() => import('../pages/financeiro/relatorios/CarteiraInvestimentosPage')));
const EvolucaoPatrimonioPage = Loadable(lazy(() => import('../pages/financeiro/relatorios/EvolucaoPatrimonioPage')));
const RentabilidadePage = Loadable(lazy(() => import('../pages/financeiro/relatorios/RentabilidadePage')));

// render - Comercial Module
const ComercialDashboardPage = Loadable(lazy(() => import('../pages/comercial/dashboard/ComercialDashboardPage')));
const ContratosListPage = Loadable(lazy(() => import('../pages/comercial/contratos/ContratosListPage')));
const ContratoForm = Loadable(lazy(() => import('../pages/comercial/contratos/ContratoForm')));
const ContratoDetalhePage = Loadable(lazy(() => import('../pages/comercial/contratos/ContratoDetalhePage')));
const ContratoTextoPage = Loadable(lazy(() => import('../pages/comercial/contratos/ContratoTextoPage')));
const RelatorioGlpiPage = Loadable(lazy(() => import('../pages/comercial/relatorios/RelatorioGlpiPage')));
const PropostasListPage = Loadable(lazy(() => import('../pages/comercial/propostas/PropostasListPage')));
const PropostaForm = Loadable(lazy(() => import('../pages/comercial/propostas/PropostaForm')));
const PropostaDetalhePage = Loadable(lazy(() => import('../pages/comercial/propostas/PropostaDetalhePage')));
const ProdutosListPage = Loadable(lazy(() => import('../pages/comercial/produtos/ProdutosListPage')));
const ProdutoFamiliasPage = Loadable(lazy(() => import('../pages/comercial/produtos/ProdutoFamiliasPage')));
const RegrasAprovacaoListPage = Loadable(lazy(() => import('../pages/comercial/configuracoes/RegrasAprovacaoListPage')));
const BibliotecaClausulasListPage = Loadable(lazy(() => import('../pages/comercial/configuracoes/BibliotecaClausulasListPage')));
const ContratoTemplatesListPage = Loadable(lazy(() => import('../pages/comercial/configuracoes/ContratoTemplatesListPage')));
const ContratoTemplateEditPage = Loadable(lazy(() => import('../pages/comercial/configuracoes/ContratoTemplateEditPage')));
const ContratoTemplateImportarDocxPage = Loadable(
  lazy(() => import('../pages/comercial/configuracoes/ContratoTemplateImportarDocxPage'))
);

// render - CRM Module
const CrmLeadsListPage = Loadable(lazy(() => import('../pages/crm/leads/LeadsListPage')));
const CrmEmailTemplatesPage = Loadable(lazy(() => import('../pages/crm/templates/EmailTemplatesPage')));
const CrmAgendamentoLinksPage = Loadable(lazy(() => import('../pages/crm/agendamento/AgendamentoLinksPage')));
const CrmWhatsAppInboxPage = Loadable(lazy(() => import('../pages/crm/whatsapp/WhatsAppInboxPage')));
const CrmDuplicadosPage = Loadable(lazy(() => import('../pages/crm/duplicados/DuplicadosPage')));
const CrmForecastPage = Loadable(lazy(() => import('../pages/crm/forecast/CrmForecastPage')));
const CrmMetasPage = Loadable(lazy(() => import('../pages/crm/metas/CrmMetasPage')));
const CrmRelatorioFunilPage = Loadable(lazy(() => import('../pages/crm/relatorios/RelatorioFunilPage')));
const CrmRelatorioAtividadePage = Loadable(lazy(() => import('../pages/crm/relatorios/RelatorioAtividadePage')));
const CrmRelatorioOrigemPage = Loadable(lazy(() => import('../pages/crm/relatorios/RelatorioOrigemPage')));
const CrmFunilKanbanPage = Loadable(lazy(() => import('../pages/crm/funil/FunilKanbanPage')));
const CrmNegociosListPage = Loadable(lazy(() => import('../pages/crm/negocios/NegociosListPage')));
const CrmAtividadesPage = Loadable(lazy(() => import('../pages/crm/atividades/AtividadesPage')));
const CrmEmpresasListPage = Loadable(lazy(() => import('../pages/crm/empresas/EmpresasListPage')));
const CrmEmpresaFichaPage = Loadable(lazy(() => import('../pages/crm/empresas/EmpresaFichaPage')));
const CrmDashboardPage = Loadable(lazy(() => import('../pages/crm/dashboard/CrmDashboardPage')));
const CrmConfiguracoesPage = Loadable(lazy(() => import('../pages/crm/configuracoes/ConfiguracoesCrmPage')));
const CrmConstrutorPage = Loadable(lazy(() => import('../pages/crm/construtor/CrmConstrutorPage')));
const CrmDashboardVendedorPage = Loadable(lazy(() => import('../pages/crm/dashboards/CrmDashboardVendedorPage')));
const CrmDashboardGestorPage = Loadable(lazy(() => import('../pages/crm/dashboards/CrmDashboardGestorPage')));
const CrmDashboardDiretoriaPage = Loadable(lazy(() => import('../pages/crm/dashboards/CrmDashboardDiretoriaPage')));
const CrmMobileShell = Loadable(lazy(() => import('../pages/crm/mobile/CrmMobileShell')));
const CrmMobileFilaPage = Loadable(lazy(() => import('../pages/crm/mobile/CrmMobileFilaPage')));
const CrmMobileFichaPage = Loadable(lazy(() => import('../pages/crm/mobile/CrmMobileFichaPage')));
const CrmPreferenciasNotificacaoPage = Loadable(lazy(() => import('../pages/crm/notificacoes/PreferenciasNotificacaoPage')));
const CrmContasAlvoPage = Loadable(lazy(() => import('../pages/crm/prospeccao/ContasAlvoPage')));
const CrmContaAlvoFichaPage = Loadable(lazy(() => import('../pages/crm/prospeccao/ContaAlvoFichaPage')));
const CrmIcpConfigPage = Loadable(lazy(() => import('../pages/crm/prospeccao/IcpConfigPage')));
const CrmLinkedInConversasPage = Loadable(lazy(() => import('../pages/crm/linkedin/ConversasLinkedInPage')));
const CrmLinkedInAprovarPage = Loadable(lazy(() => import('../pages/crm/linkedin/AprovarEnviarPage')));
const CrmLinkedInMinhaConexaoPage = Loadable(lazy(() => import('../pages/crm/linkedin/MinhaConexaoLinkedInPage')));
const CrmLinkedInConectorPage = Loadable(lazy(() => import('../pages/crm/linkedin/ConectorLinkedInPage')));
const CrmMercadoPainelPage = Loadable(lazy(() => import('../pages/crm/prospeccao/MercadoPainelPage')));
const CrmQuenteAgoraPage = Loadable(lazy(() => import('../pages/crm/prospeccao/QuenteAgoraPage')));
const CrmMobileQuenteAgoraPage = Loadable(lazy(() => import('../pages/crm/mobile/CrmMobileQuenteAgoraPage')));
const CrmRedirecionarParaQuery = Loadable(lazy(() => import('../pages/crm/RedirecionarParaQuery')));

// render - Helpdesk Module
const HelpdeskHome = Loadable(lazy(() => import('../pages/helpdesk/HelpdeskHome')));
const HelpdeskDashboardPage = Loadable(lazy(() => import('../pages/helpdesk/dashboard/HelpdeskDashboardPage')));
const HelpdeskOperacaoPage = Loadable(lazy(() => import('../pages/helpdesk/operacao/OperacaoPage')));
const HelpdeskAtividadesPeriodoPage = Loadable(lazy(() => import('../pages/helpdesk/atividades/AtividadesPeriodoPage')));
const HelpdeskConversasPage = Loadable(lazy(() => import('../pages/helpdesk/conversas/ConversasPage')));
const HelpdeskTicketsListPage = Loadable(lazy(() => import('../pages/helpdesk/tickets/TicketsListPage')));
const HelpdeskTicketDetailPage = Loadable(lazy(() => import('../pages/helpdesk/tickets/TicketDetailPage')));
const HelpdeskTicketCreatePage = Loadable(lazy(() => import('../pages/helpdesk/tickets/TicketCreatePage')));
const HelpdeskLixeiraPage = Loadable(lazy(() => import('../pages/helpdesk/tickets/LixeiraPage')));
const HelpdeskPlaceholderPage = Loadable(lazy(() => import('../pages/helpdesk/PlaceholderPage')));
const HelpdeskMailboxesPage = Loadable(lazy(() => import('../pages/helpdesk/configuracoes/MailboxesPage')));
const HelpdeskEmpresaDominiosPage = Loadable(lazy(() => import('../pages/helpdesk/configuracoes/EmpresaDominiosPage')));
const HelpdeskKanbanPage = Loadable(lazy(() => import('../pages/helpdesk/kanban/HelpdeskKanbanPage')));
const HelpdeskKbListPage = Loadable(lazy(() => import('../pages/helpdesk/kb/KbListPage')));
const HelpdeskKbEditPage = Loadable(lazy(() => import('../pages/helpdesk/kb/KbEditPage')));
const HelpdeskKbViewPage = Loadable(lazy(() => import('../pages/helpdesk/kb/KbViewPage')));
const HelpdeskCmdbListPage = Loadable(lazy(() => import('../pages/helpdesk/cmdb/AtivosListPage')));
const HelpdeskCmdbPainelPage = Loadable(lazy(() => import('../pages/helpdesk/cmdb/AtivosPainelPage')));
const HelpdeskCmdbFormPage = Loadable(lazy(() => import('../pages/helpdesk/cmdb/AtivoForm')));
const HelpdeskCmdbDetailPage = Loadable(lazy(() => import('../pages/helpdesk/cmdb/AtivoDetailPage')));
const HelpdeskCmdbImportacaoPage = Loadable(lazy(() => import('../pages/helpdesk/cmdb/importacao/AtivoImportacaoPage')));
const HelpdeskCmdbDuplicidadesPage = Loadable(lazy(() => import('../pages/helpdesk/cmdb/DuplicidadesPage')));
const HelpdeskTermosConformidadePage = Loadable(lazy(() => import('../pages/helpdesk/cmdb/TermosConformidadePage')));
const HelpdeskTermoModelosPage = Loadable(lazy(() => import('../pages/helpdesk/cmdb/TermoModelosPage')));
const HelpdeskDesligamentoPage = Loadable(lazy(() => import('../pages/helpdesk/cmdb/DesligamentoPage')));
const HelpdeskTermoModeloEditPage = Loadable(lazy(() => import('../pages/helpdesk/cmdb/TermoModeloEditPage')));
const HelpdeskContratosListPage = Loadable(lazy(() => import('../pages/helpdesk/contratos/ContratosListPage')));
const HelpdeskContratoFormPage = Loadable(lazy(() => import('../pages/helpdesk/contratos/ContratoForm')));
const HelpdeskProblemasPage = Loadable(lazy(() => import('../pages/helpdesk/itil/ProblemasPage')));
const HelpdeskRfcsPage = Loadable(lazy(() => import('../pages/helpdesk/itil/RfcsPage')));
const HelpdeskAutomacoesPage = Loadable(lazy(() => import('../pages/helpdesk/automacoes/AutomacoesPage')));
const HelpdeskRegrasChamadoPage = Loadable(lazy(() => import('../pages/helpdesk/regras/RegrasChamadoPage')));
const HelpdeskTicketTemplatesPage = Loadable(lazy(() => import('../pages/helpdesk/templates/TicketTemplatesPage')));
const HelpdeskCalendariosPage = Loadable(lazy(() => import('../pages/helpdesk/configuracoes/CalendariosPage')));
const HelpdeskSlaPage = Loadable(lazy(() => import('../pages/helpdesk/configuracoes/SlaPage')));
const HelpdeskRelatoriosHubPage = Loadable(lazy(() => import('../pages/helpdesk/relatorios/RelatoriosHubPage')));
const HelpdeskRelTicketsPorEmpresaPage = Loadable(lazy(() => import('../pages/helpdesk/relatorios/TicketsPorEmpresaPage')));
const HelpdeskRelInventarioFaturavelPage = Loadable(lazy(() => import('../pages/helpdesk/relatorios/InventarioFaturavelPage')));
const HelpdeskRelProdutividadeTecnicoPage = Loadable(lazy(() => import('../pages/helpdesk/relatorios/ProdutividadeTecnicoPage')));
const HelpdeskRelSlaCompliancePage = Loadable(lazy(() => import('../pages/helpdesk/relatorios/SlaCompliancePage')));
const HelpdeskRelBacklogAgingPage = Loadable(lazy(() => import('../pages/helpdesk/relatorios/BacklogAgingPage')));
const HelpdeskRelVolumetriaTendenciaPage = Loadable(lazy(() => import('../pages/helpdesk/relatorios/VolumetriaTendenciaPage')));
const HelpdeskRelCsatPage = Loadable(lazy(() => import('../pages/helpdesk/relatorios/CsatPage')));
const HelpdeskRelTimesheetPage = Loadable(lazy(() => import('../pages/helpdesk/relatorios/TimesheetReportPage')));
const HelpdeskRelEspelhoHorasPage = Loadable(lazy(() => import('../pages/helpdesk/relatorios/EspelhoHorasPage')));
const HelpdeskRelReaberturasPage = Loadable(lazy(() => import('../pages/helpdesk/relatorios/ReaberturasPage')));
const HelpdeskRelFornecedoresPage = Loadable(lazy(() => import('../pages/helpdesk/relatorios/FornecedoresPage')));
const HelpdeskInventarioTokensPage = Loadable(lazy(() => import('../pages/helpdesk/configuracoes/InventarioTokensPage')));
const HelpdeskCmdbCamposPage = Loadable(lazy(() => import('../pages/helpdesk/configuracoes/CmdbCamposPage')));
const HelpdeskCatalogoInventarioPage = Loadable(lazy(() => import('../pages/helpdesk/configuracoes/CatalogoInventarioPage')));
const HelpdeskUsuariosClientesPage = Loadable(lazy(() => import('../pages/helpdesk/configuracoes/UsuariosClientesPage')));
const HelpdeskCategoriasPage = Loadable(lazy(() => import('../pages/helpdesk/configuracoes/CategoriasPage')));
const HelpdeskGruposPage = Loadable(lazy(() => import('../pages/helpdesk/configuracoes/GruposPage')));
const HelpdeskConsumiveisPage = Loadable(lazy(() => import('../pages/helpdesk/consumiveis/ConsumiveisPage')));
const HelpdeskEmprestimosPage = Loadable(lazy(() => import('../pages/helpdesk/emprestimos/EmprestimosPage')));

// render - Projetos Module
const ProjetosListPage = Loadable(lazy(() => import('../pages/projetos/ProjetosListPage')));
const ProjetoLayout = Loadable(lazy(() => import('../pages/projetos/ProjetoLayout')));
const MinhasTarefasPage = Loadable(lazy(() => import('../pages/projetos/MinhasTarefasPage')));
const ProjetosRelatoriosPage = Loadable(lazy(() => import('../pages/projetos/ProjetosRelatoriosPage')));
const ProjetoRelatorioDetalhePage = Loadable(lazy(() => import('../pages/projetos/ProjetoRelatorioDetalhePage')));
const ProjetoStatusReportPage = Loadable(lazy(() => import('../pages/projetos/ProjetoStatusReportPage')));
const MeuPerfilPage = Loadable(lazy(() => import('../pages/perfil/MeuPerfilPage')));

// render - Colaboradores Module
const ColaboradoresListPage = Loadable(lazy(() => import('../pages/colaboradores/ColaboradoresListPage')));
const ColaboradorForm = Loadable(lazy(() => import('../pages/colaboradores/ColaboradorForm')));
const ColaboradorDetalhePage = Loadable(lazy(() => import('../pages/colaboradores/ColaboradorDetalhePage')));
const ColaboradoresCatalogosHubPage = Loadable(lazy(() => import('../pages/colaboradores/catalogos/CatalogosHubPage')));
const ColaboradoresCatalogoCrudPage = Loadable(lazy(() => import('../pages/colaboradores/catalogos/CatalogoCrudPage')));
const ColaboradoresCategoriasReembolsoPage = Loadable(lazy(() => import('../pages/colaboradores/catalogos/CategoriasReembolsoPage')));
const ColaboradoresReembolsosFilaPage = Loadable(lazy(() => import('../pages/colaboradores/ReembolsosFilaPage')));
const ColaboradoresPainelRhPage = Loadable(lazy(() => import('../pages/colaboradores/painel/PainelRhPage')));

// render - Portal do colaborador (/minha-area). Endereço FORA do grupo /colaboradores de
// propósito — ver o comentário no grupo de rotas lá embaixo.
const MinhaAreaResumoPage = Loadable(lazy(() => import('../pages/minha-area/ResumoPage')));
const MinhaAreaFeriasPage = Loadable(lazy(() => import('../pages/minha-area/FeriasPage')));
const MinhaAreaCompetenciasPage = Loadable(lazy(() => import('../pages/minha-area/CompetenciasPage')));
const MinhaAreaReembolsosPage = Loadable(lazy(() => import('../pages/minha-area/ReembolsosPage')));
const MinhaAreaDocumentosPage = Loadable(lazy(() => import('../pages/minha-area/DocumentosPage')));
const MinhaAreaMeusDadosPage = Loadable(lazy(() => import('../pages/minha-area/MeusDadosPage')));
const MinhaAreaEquipePage = Loadable(lazy(() => import('../pages/minha-area/EquipePage')));

// render - Cultura Module
const PesquisasClimaListPage = Loadable(lazy(() => import('../pages/cultura/PesquisasClimaListPage')));
const PesquisaClimaForm = Loadable(lazy(() => import('../pages/cultura/PesquisaClimaForm')));
const PesquisaClimaDetalhePage = Loadable(lazy(() => import('../pages/cultura/PesquisaClimaDetalhePage')));

// render - Admin
const JobsDashboardPage = Loadable(lazy(() => import('../pages/admin/JobsDashboardPage')));
const UsuariosOnlinePage = Loadable(lazy(() => import('../pages/admin/UsuariosOnlinePage')));
const IntegracoesPage = Loadable(lazy(() => import('../pages/admin/IntegracoesPage')));
const ContasServicoPage = Loadable(lazy(() => import('../pages/admin/ContasServicoPage')));
const AdminWebhooksPage = Loadable(lazy(() => import('../pages/admin/WebhooksPage')));

// render - Notificacoes Module
const NotificacoesTemplatesPage = Loadable(lazy(() => import('../pages/notificacoes/templates/TemplatesPage')));
const NotificacoesEnviosPage = Loadable(lazy(() => import('../pages/notificacoes/envios/EnviosPage')));
const NotificacoesCentralAlertasPage = Loadable(lazy(() => import('../pages/notificacoes/alertas/CentralAlertasPage')));
const MinhasNotificacoesPage = Loadable(lazy(() => import('../pages/notificacoes/minhas/MinhasNotificacoesPage')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  element: (
    <AuthGuard>
      <DashboardLayout />
    </AuthGuard>
  ),
  children: [
    {
      path: '/',
      element: <HomePage />
    },
    {
      path: 'dashboard',
      element: <HomePage />
    },
    {
      path: 'perfil',
      element: <MeuPerfilPage />
    },
    {
      path: 'agenda',
      element: <RouteGuard permissions="agenda.view"><AgendaPage /></RouteGuard>
    },
    {
      path: 'auth',
      element: <RouteGuard permissionsPrefix="auth."><Outlet /></RouteGuard>,
      children: [
        {
          path: 'usuarios',
          element: <UsersListPage />
        },
        {
          path: 'perfis',
          element: <RolesListPage />
        },
        {
          path: 'permissoes',
          element: <PermissionsListPage />
        },
        {
          path: 'auditoria',
          element: <AuthPlaceholderPage />
        }
      ]
    },
    {
      path: 'cofre',
      element: <RouteGuard permissionsPrefix="cofre."><Outlet /></RouteGuard>,
      children: [
        { path: 'credenciais', element: <CofrePage /> },
        { path: 'lixeira', element: <CofreLixeiraPage /> },
        { path: 'auditoria', element: <CofreAuditoriaPage /> }
      ]
    },
    {
      path: 'cadastros',
      element: <RouteGuard permissionsPrefix="cadastros."><Outlet /></RouteGuard>,
      children: [
        { path: 'pessoas', element: <PessoasListPage /> },
        { path: 'pessoas/:id', element: <PessoaDetailPage /> },
        { path: 'importacao', element: <PessoasImportacaoPage /> },
        {
          path: 'catalogos',
          children: [
            { path: '', element: <CatalogosHubPage /> },
            { path: 'papeis', element: <CatalogoCrudPage title="Papéis" clientKey="papeis" /> },
            { path: 'tipos-endereco', element: <CatalogoCrudPage title="Tipos de Endereço" clientKey="tiposEndereco" /> },
            { path: 'tipos-contato', element: <CatalogoCrudPage title="Tipos de Contato" clientKey="tiposContato" /> },
            { path: 'bancos', element: <CatalogoCrudPage title="Bancos" clientKey="bancos" /> },
            { path: 'estados-civis', element: <CatalogoCrudPage title="Estados Civis" clientKey="estadosCivis" /> },
            { path: 'generos', element: <CatalogoCrudPage title="Gêneros" clientKey="generos" /> },
            { path: 'profissoes', element: <CatalogoCrudPage title="Profissões" clientKey="profissoes" /> },
            { path: 'ramos-atividade', element: <CatalogoCrudPage title="Ramos de Atividade" clientKey="ramosAtividade" /> },
            { path: 'segmentos', element: <CatalogoCrudPage title="Segmentos de Mercado" clientKey="segmentos" /> },
            { path: 'regimes-tributarios', element: <CatalogoCrudPage title="Regimes Tributários" clientKey="regimesTributarios" /> },
            { path: 'lc116-itens', element: <CatalogoCrudPage title="Lista de Serviços LC 116" clientKey="lc116Itens" /> }
          ]
        }
      ]
    },
    {
      path: 'crm',
      element: <RouteGuard permissionsPrefix="crm."><Outlet /></RouteGuard>,
      children: [
        // A entrada do módulo é a fila de leads: é a tela que se abre todo dia — configurar o
        // funil é raro e tem rota própria (`configuracoes`).
        { index: true, element: <CrmLeadsListPage /> },
        { path: 'leads', element: <CrmLeadsListPage /> },
        { path: 'leads/:id', element: <CrmRedirecionarParaQuery para="/crm/leads" chave="lead" /> },
        // Gate em crm.acessar de propósito: criar o próprio template é a ação mais comum
        // e não pede permissão de configuração — essa só vale para publicar no time ou global.
        { path: 'relatorios/funil', element: <RouteGuard permissions="crm.relatorio.view"><CrmRelatorioFunilPage /></RouteGuard> },
        { path: 'relatorios/atividade', element: <RouteGuard permissions="crm.relatorio.view"><CrmRelatorioAtividadePage /></RouteGuard> },
        { path: 'relatorios/origem', element: <RouteGuard permissions="crm.relatorio.view"><CrmRelatorioOrigemPage /></RouteGuard> },
        { path: 'forecast', element: <RouteGuard permissions="crm.relatorio.view"><CrmForecastPage /></RouteGuard> },
        // Ler meta é crm.relatorio.view com escopo; a página esconde o cadastro de quem
        // não tem crm.config.manage, mas quem barra de verdade é o endpoint.
        { path: 'metas', element: <RouteGuard permissions="crm.relatorio.view"><CrmMetasPage /></RouteGuard> },
        { path: 'duplicados', element: <RouteGuard permissions="crm.lead.view"><CrmDuplicadosPage /></RouteGuard> },
        { path: 'whatsapp', element: <RouteGuard permissions="crm.atividade.view"><CrmWhatsAppInboxPage /></RouteGuard> },
        { path: 'agendamento', element: <RouteGuard permissions="crm.atividade.view"><CrmAgendamentoLinksPage /></RouteGuard> },
        { path: 'templates-email', element: <RouteGuard permissions="crm.acessar"><CrmEmailTemplatesPage /></RouteGuard> },
        { path: 'funil', element: <RouteGuard permissions="crm.negocio.view"><CrmFunilKanbanPage /></RouteGuard> },
        { path: 'negocios', element: <RouteGuard permissions="crm.negocio.view"><CrmNegociosListPage /></RouteGuard> },
        { path: 'atividades', element: <RouteGuard permissions="crm.atividade.view"><CrmAtividadesPage /></RouteGuard> },
        { path: 'empresas', element: <RouteGuard permissions="crm.empresa.view"><CrmEmpresasListPage /></RouteGuard> },
        // A ficha é por Pessoa do Cadastros (Guid), que é o id que negócio, lead e timeline
        // carregam — não há id próprio de "empresa do CRM".
        { path: 'empresas/:pessoaId', element: <RouteGuard permissions="crm.empresa.view"><CrmEmpresaFichaPage /></RouteGuard> },
        // Ver o painel é permissão própria (`crm.relatorio.view`), separada de ver negócio: ela
        // diz se a tela abre; o escopo continua decidindo o que entra na soma.
        { path: 'dashboard', element: <RouteGuard permissions="crm.relatorio.view"><CrmDashboardPage /></RouteGuard> },
        // Os três painéis por papel pedem a MESMA permissão do painel acima, de propósito: papel
        // é a PERGUNTA da tela ("o que eu faço agora" × "como está meu time" × "para onde vamos"),
        // não uma alçada. Quem separa o que cada um enxerga continua sendo o escopo — um vendedor
        // que abra /painel/gestor recebe o layout do gestor sobre a carteira dele.
        { path: 'painel/vendedor', element: <RouteGuard permissions="crm.relatorio.view"><CrmDashboardVendedorPage /></RouteGuard> },
        { path: 'painel/gestor', element: <RouteGuard permissions="crm.relatorio.view"><CrmDashboardGestorPage /></RouteGuard> },
        { path: 'painel/diretoria', element: <RouteGuard permissions="crm.relatorio.view"><CrmDashboardDiretoriaPage /></RouteGuard> },
        { path: 'construtor', element: <RouteGuard permissions="crm.relatorio.view"><CrmConstrutorPage /></RouteGuard> },
        // Prospecção (F1): a base de contas-alvo e a ficha pedem `crm.prospeccao.view` — o escopo por
        // dono quem aplica é o serviço. Desenhar o ICP é alçada própria (`crm.icp.manage`): salvar um
        // ICP recalcula a nota da base inteira.
        {
          path: 'prospeccao/contas',
          element: (
            <RouteGuard permissions="crm.prospeccao.view">
              <CrmContasAlvoPage />
            </RouteGuard>
          )
        },
        {
          path: 'prospeccao/contas/:id',
          element: (
            <RouteGuard permissions="crm.prospeccao.view">
              <CrmContaAlvoFichaPage />
            </RouteGuard>
          )
        },
        // LinkedIn (F3). A caixa abre para o dono e para a gestão (`ver_todas`, que lê a de todos);
        // aprovar envios e configurar a própria conexão são do dono (`crm.linkedin.conectar`).
        {
          path: 'linkedin/conversas',
          element: (
            <RouteGuard permissions={['crm.linkedin.conectar', 'crm.linkedin.ver_todas']}>
              <CrmLinkedInConversasPage />
            </RouteGuard>
          )
        },
        // Link do backend com o id no caminho (sino, Quente agora) → a query que a caixa lê.
        {
          path: 'linkedin/conversas/:id',
          element: <CrmRedirecionarParaQuery para="/crm/linkedin/conversas" chave="c" />
        },
        // Quente agora (F4): gate `crm.acessar`; "todas" e atribuir a outro pedem
        // `crm.quente.ver_todas`, conferido no serviço (403).
        {
          path: 'prospeccao/quente-agora',
          element: (
            <RouteGuard permissions="crm.acessar">
              <CrmQuenteAgoraPage />
            </RouteGuard>
          )
        },
        {
          path: 'linkedin/aprovar',
          element: (
            <RouteGuard permissions="crm.linkedin.conectar">
              <CrmLinkedInAprovarPage />
            </RouteGuard>
          )
        },
        {
          path: 'linkedin/minha-conexao',
          element: (
            <RouteGuard permissions="crm.linkedin.conectar">
              <CrmLinkedInMinhaConexaoPage />
            </RouteGuard>
          )
        },
        // Tamanho de mercado (F2): contagens agregadas da base, mesma permissão de ver as contas.
        {
          path: 'prospeccao/mercado',
          element: (
            <RouteGuard permissions="crm.prospeccao.view">
              <CrmMercadoPainelPage />
            </RouteGuard>
          )
        },
        {
          path: 'prospeccao/icp',
          element: (
            <RouteGuard permissions="crm.icp.manage">
              <CrmIcpConfigPage />
            </RouteGuard>
          )
        },
        // A casca mobile do vendedor (PWA). Rota-pai com layout próprio — uma coluna, alvos de
        // polegar, ações no rodapé — porque o funil e a lista de negócios não cabem em 390 px.
        // A fila sai do MESMO endpoint do painel "Meu dia", e por isso pede a mesma permissão dele;
        // cada ficha pede a permissão do registro que abre.
        {
          path: 'mobile',
          element: <CrmMobileShell />,
          children: [
            { index: true, element: <RouteGuard permissions="crm.relatorio.view"><CrmMobileFilaPage /></RouteGuard> },
            { path: 'quente-agora', element: <RouteGuard permissions="crm.acessar"><CrmMobileQuenteAgoraPage /></RouteGuard> },
            { path: 'lead/:leadId', element: <RouteGuard permissions="crm.lead.view"><CrmMobileFichaPage /></RouteGuard> },
            { path: 'negocio/:negocioId', element: <RouteGuard permissions="crm.negocio.view"><CrmMobileFichaPage /></RouteGuard> }
          ]
        },
        // `crm.acessar` e nada mais: escolher o que chega na SUA caixa não revela nem muda nada do
        // processo. Exigir `crm.config.manage` faria o vendedor, que é quem recebe os avisos, ser o
        // único que não pode desligá-los.
        { path: 'notificacoes', element: <RouteGuard permissions="crm.acessar"><CrmPreferenciasNotificacaoPage /></RouteGuard> },
        // Configuração é TRÊS alçadas: desenhar o processo (`crm.config.manage`), administrar o
        // escopo de visibilidade (`crm.time.manage`) e a configuração PESSOAL de quem usa o módulo
        // (`crm.acessar` — a própria caixa de e-mail e a assinatura dela). A lista é OR: quem tem
        // uma entra e vê só a aba dela; a página esconde as outras. `crm.acessar` entra porque
        // ligar a própria caixa não é ato administrativo, e exigir alçada de processo faria o
        // vendedor, que é o dono da caixa, ser o único que não pode conectá-la.
        {
          path: 'configuracoes',
          element: (
            <RouteGuard permissions={['crm.config.manage', 'crm.time.manage', 'crm.acessar']}>
              <CrmConfiguracoesPage />
            </RouteGuard>
          )
        }
      ]
    },
    {
      path: 'comercial',
      element: <RouteGuard permissionsPrefix="comercial."><Outlet /></RouteGuard>,
      children: [
        { path: 'dashboard', element: <ComercialDashboardPage /> },
        { path: 'propostas', element: <PropostasListPage /> },
        { path: 'propostas/nova', element: <PropostaForm /> },
        { path: 'propostas/:id', element: <PropostaDetalhePage /> },
        { path: 'propostas/:id/editar', element: <PropostaForm /> },
        { path: 'contratos', element: <ContratosListPage /> },
        { path: 'contratos/novo', element: <ContratoForm /> },
        { path: 'contratos/:id', element: <ContratoDetalhePage /> },
        { path: 'contratos/:id/editar', element: <ContratoForm /> },
        // Só o texto do contrato: documento de 30 páginas com o painel do que falta preencher.
        { path: 'contratos/:id/texto', element: <ContratoTextoPage /> },
        { path: 'produtos', element: <ProdutosListPage /> },
        { path: 'produto-familias', element: <ProdutoFamiliasPage /> },
        { path: 'relatorios/glpi', element: <RelatorioGlpiPage /> },
        {
          path: 'configuracoes',
          children: [
            { path: 'regras-aprovacao', element: <RegrasAprovacaoListPage /> },
            { path: 'biblioteca-clausulas', element: <BibliotecaClausulasListPage /> },
            { path: 'contrato-templates', element: <ContratoTemplatesListPage /> },
            { path: 'contrato-templates/importar', element: <ContratoTemplateImportarDocxPage /> },
            // 'novo' antes de ':id' porque a rota estática tem de ganhar do parâmetro.
            { path: 'contrato-templates/novo', element: <ContratoTemplateEditPage /> },
            { path: 'contrato-templates/:id', element: <ContratoTemplateEditPage /> }
          ]
        }
      ]
    },
    {
      path: 'helpdesk',
      element: <RouteGuard permissionsPrefix="helpdesk."><Outlet /></RouteGuard>,
      children: [
        { index: true, element: <HelpdeskHome /> },
        { path: 'dashboard', element: <HelpdeskDashboardPage /> },
        { path: 'operacao', element: <RouteGuard permissions="helpdesk.ticket.view"><HelpdeskOperacaoPage /></RouteGuard> },
        { path: 'conversas', element: <RouteGuard permissions="helpdesk.whatsapp.atender"><HelpdeskConversasPage /></RouteGuard> },
        { path: 'conversas/:id', element: <RouteGuard permissions="helpdesk.whatsapp.atender"><HelpdeskConversasPage /></RouteGuard> },
        { path: 'tickets', element: <HelpdeskTicketsListPage /> },
        { path: 'tickets/novo', element: <HelpdeskTicketCreatePage /> },
        { path: 'tickets/lixeira', element: <HelpdeskLixeiraPage /> },
        { path: 'tickets/:id', element: <HelpdeskTicketDetailPage /> },
        { path: 'atividades', element: <RouteGuard permissions="helpdesk.ticket.view"><HelpdeskAtividadesPeriodoPage /></RouteGuard> },
        { path: 'kanban', element: <HelpdeskKanbanPage /> },
        { path: 'kb', element: <HelpdeskKbListPage /> },
        { path: 'kb/novo', element: <HelpdeskKbEditPage /> },
        { path: 'kb/:id', element: <HelpdeskKbViewPage /> },
        { path: 'kb/:id/editar', element: <HelpdeskKbEditPage /> },
        { path: 'cmdb', element: <HelpdeskCmdbListPage /> },
        { path: 'cmdb/painel', element: <RouteGuard permissions="helpdesk.cmdb.view"><HelpdeskCmdbPainelPage /></RouteGuard> },
        { path: 'cmdb/novo', element: <HelpdeskCmdbFormPage /> },
        { path: 'cmdb/importacao', element: <RouteGuard permissions="helpdesk.cmdb.import"><HelpdeskCmdbImportacaoPage /></RouteGuard> },
        { path: 'cmdb/duplicidades', element: <RouteGuard permissions="helpdesk.cmdb.view"><HelpdeskCmdbDuplicidadesPage /></RouteGuard> },
        { path: 'cmdb/conformidade', element: <RouteGuard permissions="helpdesk.cmdb.view"><HelpdeskTermosConformidadePage /></RouteGuard> },
        { path: 'cmdb/desligamento', element: <RouteGuard permissions="helpdesk.cmdb.view"><HelpdeskDesligamentoPage /></RouteGuard> },
        { path: 'cmdb/termo-modelos', element: <RouteGuard permissions="helpdesk.cmdb.view"><HelpdeskTermoModelosPage /></RouteGuard> },
        { path: 'cmdb/termo-modelos/novo', element: <RouteGuard permissions="helpdesk.cmdb.manage"><HelpdeskTermoModeloEditPage /></RouteGuard> },
        { path: 'cmdb/termo-modelos/:id', element: <RouteGuard permissions="helpdesk.cmdb.view"><HelpdeskTermoModeloEditPage /></RouteGuard> },
        { path: 'cmdb/:id', element: <HelpdeskCmdbDetailPage /> },
        { path: 'contratos', element: <HelpdeskContratosListPage /> },
        { path: 'contratos/novo', element: <HelpdeskContratoFormPage /> },
        { path: 'contratos/:id', element: <HelpdeskContratoFormPage /> },
        { path: 'consumiveis', element: <HelpdeskConsumiveisPage /> },
        { path: 'emprestimos', element: <HelpdeskEmprestimosPage /> },
        { path: 'itil/problemas', element: <HelpdeskProblemasPage /> },
        { path: 'itil/rfcs', element: <HelpdeskRfcsPage /> },
        { path: 'automacoes', element: <HelpdeskAutomacoesPage /> },
        { path: 'regras-chamado', element: <HelpdeskRegrasChamadoPage /> },
        { path: 'templates', element: <HelpdeskTicketTemplatesPage /> },
        { path: 'configuracoes/calendarios', element: <HelpdeskCalendariosPage /> },
        { path: 'configuracoes/categorias', element: <HelpdeskCategoriasPage /> },
        { path: 'configuracoes/grupos', element: <HelpdeskGruposPage /> },
        { path: 'configuracoes/tags', element: <HelpdeskPlaceholderPage titulo="Tags" /> },
        { path: 'configuracoes/sla', element: <HelpdeskSlaPage /> },
        { path: 'configuracoes/mailboxes', element: <HelpdeskMailboxesPage /> },
        { path: 'configuracoes/dominios-cliente', element: <HelpdeskEmpresaDominiosPage /> },
        { path: 'configuracoes/inventario-tokens', element: <HelpdeskInventarioTokensPage /> },
        { path: 'configuracoes/cmdb-campos', element: <HelpdeskCmdbCamposPage /> },
        { path: 'configuracoes/catalogo-inventario', element: <HelpdeskCatalogoInventarioPage /> },
        { path: 'configuracoes/usuarios-clientes', element: <HelpdeskUsuariosClientesPage /> },
        { path: 'relatorios', element: <HelpdeskRelatoriosHubPage /> },
        { path: 'relatorios/tickets-por-empresa', element: <HelpdeskRelTicketsPorEmpresaPage /> },
        { path: 'relatorios/inventario-faturavel', element: <HelpdeskRelInventarioFaturavelPage /> },
        { path: 'relatorios/produtividade-tecnico', element: <HelpdeskRelProdutividadeTecnicoPage /> },
        { path: 'relatorios/sla-compliance', element: <HelpdeskRelSlaCompliancePage /> },
        { path: 'relatorios/backlog-aging', element: <HelpdeskRelBacklogAgingPage /> },
        { path: 'relatorios/volumetria-tendencia', element: <HelpdeskRelVolumetriaTendenciaPage /> },
        { path: 'relatorios/csat', element: <HelpdeskRelCsatPage /> },
        { path: 'relatorios/timesheet', element: <HelpdeskRelTimesheetPage /> },
        { path: 'relatorios/espelho-horas', element: <HelpdeskRelEspelhoHorasPage /> },
        { path: 'relatorios/reaberturas', element: <HelpdeskRelReaberturasPage /> },
        { path: 'relatorios/fornecedores', element: <HelpdeskRelFornecedoresPage /> }
      ]
    },
    {
      path: 'projetos',
      element: <RouteGuard permissionsPrefix="projetos."><Outlet /></RouteGuard>,
      children: [
        { path: '', element: <ProjetosListPage /> },
        { path: 'arquivados', element: <ProjetosListPage arquivados /> },
        { path: 'relatorios', element: <ProjetosRelatoriosPage /> },
        { path: 'relatorios/:projetoId', element: <ProjetoRelatorioDetalhePage /> },
        { path: 'minhas-tarefas', element: <MinhasTarefasPage /> },
        {
          // Casca do projeto: o cabeçalho fica montado e só a visão troca.
          path: ':id',
          element: <ProjetoLayout />,
          children: [
            // Sem a visão no endereço (/projetos/:id), ou com uma que não existe (digitada errado), a casca
            // leva ao modo de visualização da pessoa, com o ?tarefa= e o resto do endereço.
            { index: true, element: null },
            // As visões vêm sem o Loadable: o Suspense delas fica na casca, com o esqueleto da visão.
            { path: 'lista', element: <VisaoLista /> },
            { path: 'kanban', element: <VisaoKanban /> },
            { path: 'gantt', element: <VisaoGantt /> },
            { path: 'calendar', element: <VisaoCalendario /> },
            { path: '*', element: null }
          ]
        },
        // A apresentação não é uma visão do projeto: fica fora da casca.
        { path: ':id/status-report', element: <ProjetoStatusReportPage /> }
      ]
    },
    {
      // As telas do RH.
      //
      // **O guard deixou de ser `permissionsPrefix="colaboradores."`**, e a razão é a fatia do
      // portal: aquele prefixo casa com `colaboradores.self.*` também, então um prestador com a
      // role Colaborador atravessaria este guard e abriria a lista de colaboradores, o dossiê de
      // qualquer pessoa e a remuneração de todo mundo. O guard libera por prefixo e não há como
      // escrever "menos este" — então ele enumera as famílias do RH. Família nova no módulo que
      // deva abrir estas telas precisa ser acrescentada aqui; é um custo pequeno e explícito,
      // pago para que a família do prestador não abra a porta do RH por omissão.
      path: 'colaboradores',
      element: (
        <RouteGuard
          permissionsPrefix={[
            'colaboradores.colaboradores.',
            'colaboradores.vinculos.',
            'colaboradores.mudancas.',
            'colaboradores.documentos.',
            'colaboradores.ausencias.',
            'colaboradores.notas-fiscais.',
            'colaboradores.reembolsos.',
            'colaboradores.avaliacoes.',
            'colaboradores.catalogos.',
            'colaboradores.remuneracao.',
            'colaboradores.painel.'
          ]}
        >
          <Outlet />
        </RouteGuard>
      ),
      children: [
        // A porta do RH: abre no que precisa de ação, não na lista de gente.
        { path: 'painel', element: <ColaboradoresPainelRhPage /> },
        { path: 'colaboradores', element: <ColaboradoresListPage /> },
        { path: 'colaboradores/novo', element: <ColaboradorForm /> },
        { path: 'colaboradores/:id', element: <ColaboradorDetalhePage /> },
        // A fila de reembolso do RH: a metade que a F4 entregou sem tela.
        { path: 'reembolsos', element: <ColaboradoresReembolsosFilaPage /> },
        {
          path: 'catalogos',
          children: [
            { path: '', element: <ColaboradoresCatalogosHubPage /> },
            { path: 'cargos', element: <ColaboradoresCatalogoCrudPage title="Cargos" catalogoKey="cargos" /> },
            { path: 'areas', element: <ColaboradoresCatalogoCrudPage title="Áreas" catalogoKey="areas" /> },
            { path: 'tipos-documento', element: <ColaboradoresCatalogoCrudPage title="Tipos de documento" catalogoKey="tiposDocumento" /> },
            { path: 'tipos-avaliacao', element: <ColaboradoresCatalogoCrudPage title="Tipos de avaliação" catalogoKey="tiposAvaliacao" /> },
            // Fora do CRUD genérico porque tem um terceiro campo com semântica própria: a
            // categoria do Financeiro em que o título do reembolso nasce.
            { path: 'categorias-reembolso', element: <ColaboradoresCategoriasReembolsoPage /> }
          ]
        }
      ]
    },
    {
      // ─── Portal do colaborador ────────────────────────────────────────────────
      //
      // **Grupo próprio, e não `/colaboradores/eu`.** O guard do grupo acima libera por PREFIXO:
      // qualquer rota aninhada nele herda "quem tem alguma permissão da lista entra". Pendurar o
      // portal ali dentro significaria que a role Colaborador — que tem só `self.*` — atravessaria
      // o mesmo portão das telas do RH, e a separação dependeria de cada tela se defender sozinha.
      // Endereço separado faz a separação ser estrutural: o prestador nem chega perto.
      //
      // O guard aqui é `colaboradores.self.` por prefixo porque as seis telas têm permissões
      // diferentes e qualquer uma delas deve abrir a área; quem só pede férias cai no resumo e vê
      // o menu com o que é dele. A aba Equipe exige `equipe.*`, que é outra autoridade.
      path: 'minha-area',
      element: <RouteGuard permissionsPrefix="colaboradores.self."><Outlet /></RouteGuard>,
      children: [
        { path: '', element: <MinhaAreaResumoPage /> },
        { path: 'ferias', element: <MinhaAreaFeriasPage /> },
        { path: 'competencias', element: <MinhaAreaCompetenciasPage /> },
        { path: 'reembolsos', element: <MinhaAreaReembolsosPage /> },
        { path: 'documentos', element: <MinhaAreaDocumentosPage /> },
        { path: 'meus-dados', element: <MinhaAreaMeusDadosPage /> },
        {
          // A tela do gestor. Guard próprio ANINHADO: o grupo já exigiu `self.*` (todo gestor é
          // colaborador) e aqui se exige também `equipe.*` — os guards aninhados combinam em AND,
          // que é o que se quer. Sem ele, um prestador qualquer abriria a tela e veria uma equipe
          // vazia; o 403 do servidor seguraria o dado, mas a tela existiria sem razão.
          path: 'equipe',
          element: (
            <RouteGuard permissionsPrefix="colaboradores.equipe.">
              <MinhaAreaEquipePage />
            </RouteGuard>
          )
        }
      ]
    },
    {
      path: 'cultura',
      element: <RouteGuard permissionsPrefix="cultura."><Outlet /></RouteGuard>,
      children: [
        { path: 'pesquisas-clima', element: <PesquisasClimaListPage /> },
        { path: 'pesquisas-clima/nova', element: <PesquisaClimaForm /> },
        { path: 'pesquisas-clima/:id', element: <PesquisaClimaDetalhePage /> },
        { path: 'pesquisas-clima/:id/editar', element: <PesquisaClimaForm /> }
      ]
    },
    // Caixa pessoal: fora do guard de módulo — todo usuário logado tem a própria caixa.
    { path: 'minhas-notificacoes', element: <MinhasNotificacoesPage /> },
    {
      path: 'notificacoes',
      element: <RouteGuard permissionsPrefix="notificacoes."><Outlet /></RouteGuard>,
      children: [
        { path: 'envios', element: <NotificacoesEnviosPage /> },
        { path: 'templates', element: <NotificacoesTemplatesPage /> },
        { path: 'alertas', element: <NotificacoesCentralAlertasPage /> }
      ]
    },
    {
      path: 'admin',
      // Dois critérios em OR: o prefixo `admin.` cobre as telas do grupo, e `auth.servicos.manage`
      // entra à parte porque a permissão de contas de serviço mora no Auth (o que ela libera é
      // emitir credencial). Toda permissão que abra uma tela daqui sem ser `admin.*` precisa
      // aparecer nesta lista, senão o guard do grupo barra antes de a rota filha ser avaliada.
      element: (
        <RouteGuard permissionsPrefix="admin." permissions={['auth.servicos.manage', 'crm.linkedin.ver_todas']}>
          <Outlet />
        </RouteGuard>
      ),
      children: [
        { path: 'jobs', element: <RouteGuard permissions="admin.jobs.view"><JobsDashboardPage /></RouteGuard> },
        { path: 'usuarios-online', element: <RouteGuard permissions="admin.usuarios.view"><UsuariosOnlinePage /></RouteGuard> },
        { path: 'integracoes', element: <RouteGuard permissions="admin.integracoes.manage"><IntegracoesPage /></RouteGuard> },
        // O conector do LinkedIn mora em Integrações, mas a alçada é do CRM — por isso
        // `crm.linkedin.ver_todas` entrou também no guard do grupo, logo acima.
        { path: 'integracoes/linkedin', element: <RouteGuard permissions="crm.linkedin.ver_todas"><CrmLinkedInConectorPage /></RouteGuard> },
        { path: 'servicos', element: <RouteGuard permissions="auth.servicos.manage"><ContasServicoPage /></RouteGuard> },
        // Veio do CRM: o motor de webhook virou da casa (módulo Integrações) e as rotas são
        // `/api/admin/webhooks…`. O histórico mostra o corpo dos eventos de qualquer cliente, então
        // a leitura pede a mesma permissão da configuração.
        { path: 'webhooks', element: <RouteGuard permissions="admin.webhooks.manage"><AdminWebhooksPage /></RouteGuard> }
      ]
    },
    {
      path: 'financeiro',
      element: <RouteGuard permissionsPrefix="financeiro."><Outlet /></RouteGuard>,
      children: [
        { path: 'dashboard', element: <DashboardFinanceiroPage /> },
        { path: 'lancamentos', element: <LancamentosListPage /> },
        { path: 'faturamento', element: <RouteGuard permissions="financeiro.faturamento.view"><PainelFaturamentoPage /></RouteGuard> },
        { path: 'faturamento/perfis', element: <RouteGuard permissions="financeiro.faturamento.configurar"><FaturamentoPerfisPage /></RouteGuard> },
        { path: 'faturamento/atividades-fiscais', element: <RouteGuard permissions="financeiro.faturamento.configurar"><AtividadesFiscaisPage /></RouteGuard> },
        { path: 'integracoes/asaas', element: <RouteGuard permissions="financeiro.faturamento.configurar"><AsaasIntegracaoPage /></RouteGuard> },
        { path: 'faturas', element: <FaturasListPage /> },
        { path: 'faturas/:id', element: <FaturaDetailPage /> },
        { path: 'contas-pagar', element: <ContasPagarListPage /> },
        { path: 'contas-receber', element: <ContasReceberListPage /> },
        { path: 'pagamentos', element: <RouteGuard permissions="financeiro.pagamentos.view"><PagamentosPage /></RouteGuard> },
        { path: 'categorias', element: <CategoriasPage /> },
        { path: 'centros-custo', element: <CentrosCustoPage /> },
        { path: 'contas-bancarias', element: <ContasBancariasPage /> },
        { path: 'investimentos', element: <InvestimentosPage /> },
        { path: 'bancos', element: <BancosPage /> },
        { path: 'conciliacao', element: <ConciliacaoPage /> },
        { path: 'fechamentos', element: <FechamentosPage /> },
        { path: 'importacao', element: <ImportacaoPage /> },
        { path: 'orcamento', element: <RouteGuard permissions="financeiro.categorias.view"><OrcamentoPage /></RouteGuard> },
        {
          path: 'relatorios',
          children: [
            { path: '', element: <RelatoriosHubPage /> },
            { path: 'fluxo-caixa', element: <FluxoCaixaPage /> },
            { path: 'dre', element: <DREPage /> },
            { path: 'aging', element: <AgingPage /> },
            { path: 'por-categoria', element: <PorCategoriaPage /> },
            { path: 'por-centro-custo', element: <PorCentroCustoPage /> },
            { path: 'previsto-realizado', element: <PrevistoRealizadoPage /> },
            { path: 'orcado-realizado', element: <OrcadoRealizadoPage /> },
            { path: 'evolucao', element: <EvolucaoMensalPage /> },
            { path: 'saldos-contas', element: <SaldosContasPage /> },
            { path: 'posicoes-investimentos', element: <PosicoesInvestimentosPage /> },
            { path: 'patrimonio-consolidado', element: <PatrimonioConsolidadoPage /> },
            { path: 'carteira-investimentos', element: <CarteiraInvestimentosPage /> },
            { path: 'evolucao-patrimonio', element: <EvolucaoPatrimonioPage /> },
            { path: 'rentabilidade', element: <RentabilidadePage /> }
          ]
        }
      ]
    }
  ]
};

export default MainRoutes;
